import { TachoRosterApi } from '@repo/api'
import {
  AppFeature,
  ScheduleEntityType,
  TachoDownloadStatus,
} from '@repo/shared'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Input,
  Skeleton,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@repo/ui'
import { useDebouncedValue } from '@tanstack/react-pacer'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { CalendarClock, Search, Truck, User } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import type { SortState } from '@/components/shared/sortable-table-head'
import type { RosterFilterPatch } from '@/features/tachograph/components/tacho-roster-filters'
import type {
  TachoRosterFilters as RosterFilterValues,
  ScheduleAssignment,
  TachoRosterDriverItem,
  TachoRosterVehicleItem,
} from '@repo/shared'
import type { LucideIcon } from 'lucide-react'

import PageHeader from '@/components/layout/page-header'
import { ConfirmDeleteDialog } from '@/components/shared/confirm-delete-dialog'
import { FeatureGate } from '@/components/shared/feature-gate'
import { ListPagination } from '@/components/shared/list-pagination'
import {
  SortableTableHead,
  sortItems,
  toggleSort,
} from '@/components/shared/sortable-table-head'
import { QuickScheduleModal } from '@/features/tachograph/components/quick-schedule-modal'
import { RosterSelectionBar } from '@/features/tachograph/components/roster-selection-bar'
import { SchedulesModal } from '@/features/tachograph/components/schedules-modal'
import { TachoRosterFilters } from '@/features/tachograph/components/tacho-roster-filters'
import { TachographDriverCard } from '@/features/tachograph/components/tachograph-driver-card'
import { TachographDriverTableRow } from '@/features/tachograph/components/tachograph-driver-table-row'
import { TachographVehicleCard } from '@/features/tachograph/components/tachograph-vehicle-card'
import { TachographVehicleTableRow } from '@/features/tachograph/components/tachograph-vehicle-table-row'
import {
  useBulkRemoveAssignments,
  useGetScheduleAssignments,
  useTachoRoster,
} from '@/features/tachograph/hooks'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { localizedPageTitle } from '@/lib/page-title'

const searchSchema = z.object({
  tab: z.enum(['vehicle', 'driver']).optional(),
  search: z.string().optional(),
  downloadStatuses: z.array(z.nativeEnum(TachoDownloadStatus)).optional(),
  statuses: z.array(z.string()).optional(),
  scheduleIds: z.array(z.string()).optional(),
  noDeviceImei: z.boolean().optional(),
  noSchedulesAssigned: z.boolean().optional(),
})

export const Route = createFileRoute('/dashboard/tachograph-activities/')({
  head: () => ({
    meta: [{ title: localizedPageTitle('tachographActivities') }],
  }),
  component: () => (
    <FeatureGate feature={AppFeature.HosData}>
      <TachographActivitiesPage />
    </FeatureGate>
  ),
  validateSearch: searchSchema,
})

const vehicleAccessor = (item: TachoRosterVehicleItem, key: string) => {
  switch (key) {
    case 'licensePlate':
      return item.licensePlate
    case 'lastDownload':
      return item.lastDownloadAt ? Date.parse(item.lastDownloadAt) : null
    case 'vin':
      return item.vin
    case 'status':
      return item.status
    default:
      return null
  }
}

const driverAccessor = (item: TachoRosterDriverItem, key: string) => {
  switch (key) {
    case 'name':
      return `${item.firstName} ${item.lastName}`
    case 'lastDownload':
      return item.lastDownloadAt ? Date.parse(item.lastDownloadAt) : null
    case 'tachographCard':
      return item.tachographCardId
    case 'license':
      return item.licenseNumber
    case 'status':
      return item.status
    default:
      return null
  }
}

function TachographActivitiesPage() {
  useDocumentTitle('tachographActivities')
  const { t } = useTranslation('tachographActivities')
  const { t: ts } = useTranslation('tachographSchedules')
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })

  const tab = search.tab ?? 'vehicle'
  const searchQuery = search.search ?? ''
  const [debouncedSearch] = useDebouncedValue(searchQuery, { wait: 300 })

  const [vehiclePage, setVehiclePage] = useState(1)
  const [vehiclePageSize, setVehiclePageSize] = useState(10)
  const [driverPage, setDriverPage] = useState(1)
  const [driverPageSize, setDriverPageSize] = useState(10)
  const [vehicleSort, setVehicleSort] = useState<SortState>(null)
  const [driverSort, setDriverSort] = useState<SortState>(null)

  const [selectedVehicleIds, setSelectedVehicleIds] = useState<Array<number>>(
    [],
  )
  const [selectedDriverIds, setSelectedDriverIds] = useState<Array<number>>([])
  const [schedulesOpen, setSchedulesOpen] = useState(false)
  // Quick (one-time) download target: which internal ids, and whether the fire came
  // from the multi-select bar (→ clear selection on success) or a single row (→ not).
  const [quickOpen, setQuickOpen] = useState(false)
  const [quickTarget, setQuickTarget] = useState<{
    ids: Array<number>
    fromSelection: boolean
  }>({ ids: [], fromSelection: false })

  const scheduleType =
    tab === 'vehicle' ? ScheduleEntityType.Vehicle : ScheduleEntityType.Driver
  const { data: scheduleAssignments, isLoading: assignmentsLoading } =
    useGetScheduleAssignments(scheduleType)
  const assignmentMap = useMemo(() => {
    const map = new Map<number, ScheduleAssignment>()
    for (const a of scheduleAssignments ?? []) map.set(a.entityId, a)
    return map
  }, [scheduleAssignments])

  // Filter values for the current tab; vehicle-only filters are stripped on the
  // driver tab so stale URL params can never leak into the driver query.
  const filterValues = useMemo<Omit<RosterFilterValues, 'search'>>(
    () => ({
      downloadStatuses: search.downloadStatuses,
      statuses: search.statuses,
      scheduleIds: search.scheduleIds,
      noDeviceImei: tab === 'vehicle' ? search.noDeviceImei : undefined,
      noSchedulesAssigned: search.noSchedulesAssigned,
    }),
    [
      tab,
      search.downloadStatuses,
      search.statuses,
      search.scheduleIds,
      search.noDeviceImei,
      search.noSchedulesAssigned,
    ],
  )

  const activeFilters = useMemo<RosterFilterValues>(
    () => ({ search: debouncedSearch || undefined, ...filterValues }),
    [debouncedSearch, filterValues],
  )

  const hasActiveFilters =
    Boolean(debouncedSearch) ||
    Boolean(filterValues.downloadStatuses?.length) ||
    Boolean(filterValues.statuses?.length) ||
    Boolean(filterValues.scheduleIds?.length) ||
    Boolean(filterValues.noDeviceImei) ||
    Boolean(filterValues.noSchedulesAssigned)

  const { data: vehiclesData, isLoading: vehiclesLoading } = useTachoRoster(
    ScheduleEntityType.Vehicle,
    {
      page: vehiclePage,
      pageSize: vehiclePageSize,
      ...(tab === 'vehicle' ? activeFilters : {}),
    },
  )

  const { data: driversData, isLoading: driversLoading } = useTachoRoster(
    ScheduleEntityType.Driver,
    {
      page: driverPage,
      pageSize: driverPageSize,
      ...(tab === 'driver' ? activeFilters : {}),
    },
  )

  // Single write-path for URL filter state — every change resets both pages so
  // the user never lands on an out-of-range page after narrowing.
  const setFilters = useCallback(
    (patch: RosterFilterPatch & { search?: string }) => {
      navigate({
        search: (prev) => ({ ...prev, ...patch }),
        replace: true,
      })
      setVehiclePage(1)
      setDriverPage(1)
    },
    [navigate],
  )

  const handleTabChange = (value: string) => {
    navigate({
      search: {
        tab: value as 'vehicle' | 'driver',
        search: undefined,
        downloadStatuses: undefined,
        statuses: undefined,
        scheduleIds: undefined,
        noDeviceImei: undefined,
        noSchedulesAssigned: undefined,
      },
      replace: true,
    })
    setVehiclePage(1)
    setDriverPage(1)
  }

  const handleSearchChange = (value: string) =>
    setFilters({ search: value || undefined })

  const handleVehicleSort = (key: string) =>
    setVehicleSort((prev) => toggleSort(prev, key))

  const handleDriverSort = (key: string) =>
    setDriverSort((prev) => toggleSort(prev, key))

  // The roster endpoint returns a vehicle/driver union — narrow per tab.
  const vehicleItems = useMemo(
    () => (vehiclesData?.items ?? []) as Array<TachoRosterVehicleItem>,
    [vehiclesData?.items],
  )
  const driverItems = useMemo(
    () => (driversData?.items ?? []) as Array<TachoRosterDriverItem>,
    [driversData?.items],
  )

  const sortedVehicles = useMemo(
    () => sortItems(vehicleItems, vehicleSort, vehicleAccessor),
    [vehicleItems, vehicleSort],
  )

  const sortedDrivers = useMemo(
    () => sortItems(driverItems, driverSort, driverAccessor),
    [driverItems, driverSort],
  )

  const vehicleIds = useMemo(
    () => sortedVehicles.map((v) => v.id),
    [sortedVehicles],
  )
  const driverIds = useMemo(
    () => sortedDrivers.map((d) => d.id),
    [sortedDrivers],
  )

  const allVehiclesSelected =
    vehicleIds.length > 0 &&
    vehicleIds.every((id) => selectedVehicleIds.includes(id))
  const allDriversSelected =
    driverIds.length > 0 &&
    driverIds.every((id) => selectedDriverIds.includes(id))

  const toggleVehicle = (id: number, checked: boolean) =>
    setSelectedVehicleIds((prev) =>
      checked ? [...new Set([...prev, id])] : prev.filter((x) => x !== id),
    )
  const toggleDriver = (id: number, checked: boolean) =>
    setSelectedDriverIds((prev) =>
      checked ? [...new Set([...prev, id])] : prev.filter((x) => x !== id),
    )

  const toggleAllVehicles = (checked: boolean) =>
    setSelectedVehicleIds((prev) =>
      checked
        ? [...new Set([...prev, ...vehicleIds])]
        : prev.filter((id) => !vehicleIds.includes(id)),
    )
  const toggleAllDrivers = (checked: boolean) =>
    setSelectedDriverIds((prev) =>
      checked
        ? [...new Set([...prev, ...driverIds])]
        : prev.filter((id) => !driverIds.includes(id)),
    )

  // "Filtered" = every row matching the active search + filters, across ALL
  // pages. Count comes from the paginated total; ids are resolved on demand by
  // the dedicated ids endpoint at apply time.
  const filteredCount = hasActiveFilters
    ? ((tab === 'vehicle'
        ? vehiclesData?.totalCount
        : driversData?.totalCount) ?? 0)
    : 0

  const resolveFilteredIds = useCallback(async (): Promise<Array<number>> => {
    if (!hasActiveFilters) return []
    const res = await TachoRosterApi.ids(scheduleType, activeFilters)
    return res.ids
  }, [hasActiveFilters, scheduleType, activeFilters])

  // Bulk-selection actions for the active tab. Selection persists across tabs,
  // but only the mounted tab's bar is rendered, so a single set of tab-aware
  // handlers is enough.
  const bulkRemove = useBulkRemoveAssignments(scheduleType)
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false)

  const selectedIds = tab === 'vehicle' ? selectedVehicleIds : selectedDriverIds
  const clearSelection = () =>
    tab === 'vehicle' ? setSelectedVehicleIds([]) : setSelectedDriverIds([])

  // Flatten selected rows into (entity, schedule) pairs to detach, and count the
  // distinct entities that actually have something to remove (for the message +
  // to disable the button when the selection carries no schedules).
  const removePairs = useMemo(() => {
    const pairs: Array<{ entityId: number; scheduleId: string }> = []
    for (const id of selectedIds) {
      const assignment = assignmentMap.get(id)
      for (const scheduleId of assignment?.scheduleIds ?? []) {
        pairs.push({ entityId: id, scheduleId })
      }
    }
    return pairs
  }, [selectedIds, assignmentMap])

  const removableEntityCount = useMemo(
    () =>
      selectedIds.filter(
        (id) => (assignmentMap.get(id)?.scheduleIds.length ?? 0) > 0,
      ).length,
    [selectedIds, assignmentMap],
  )

  const handleConfirmRemove = () =>
    bulkRemove.mutate(
      { pairs: removePairs, entityCount: removableEntityCount },
      {
        onSuccess: ({ total, failed }) => {
          setRemoveConfirmOpen(false)
          // Keep the selection when nothing succeeded so the user can retry
          // in place; clear it once at least one detach went through.
          if (failed < total) clearSelection()
        },
      },
    )

  const openQuick = (ids: Array<number>, fromSelection: boolean) => {
    setQuickTarget({ ids, fromSelection })
    setQuickOpen(true)
  }

  const selectionBar =
    selectedIds.length > 0 ? (
      <RosterSelectionBar
        count={selectedIds.length}
        removeDisabled={removePairs.length === 0}
        removing={bulkRemove.isPending}
        onQuickSchedule={() => openQuick(selectedIds, true)}
        onApplySchedule={() => setSchedulesOpen(true)}
        onRemoveSchedules={() => setRemoveConfirmOpen(true)}
        onDeselectAll={clearSelection}
      />
    ) : null

  return (
    <div className="space-y-4 md:space-y-6">
      <PageHeader
        title={t('pageTitle')}
        subtitle={t('pageSubtitle')}
        hideBackButton
      />

      <Card className="bg-background w-full max-md:bg-transparent max-md:shadow-none">
        <Tabs value={tab} onValueChange={handleTabChange}>
          <CardHeader className="max-md:px-2">
            <div className="flex w-full flex-col items-start justify-between gap-3 sm:items-center sm:gap-4 lg:flex-row">
              <div className="flex w-full flex-1 items-center gap-2">
                <TabsList variant="icon" className="shrink-0 sm:w-auto">
                  <TabsTrigger
                    value="vehicle"
                    aria-label={t('vehicleTab')}
                    variant="icon"
                  >
                    <Truck className="size-4" />
                  </TabsTrigger>
                  <TabsTrigger
                    value="driver"
                    aria-label={t('driverTab')}
                    variant="icon"
                  >
                    <User className="size-4" />
                  </TabsTrigger>
                </TabsList>
                <div className="relative w-full sm:min-w-48 sm:flex-1">
                  <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
                  <Input
                    placeholder={t('searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
              <div className="flex w-full min-w-0 flex-1 justify-end gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                {/* Mobile: buttons form a fixed 3-col grid so they never reflow
                    into an orphan icon-only row (sm:contents drops the grid on
                    desktop, restoring the original flex-wrap layout). */}
                <div className="grid w-full grid-cols-[1fr_1fr_auto_1fr] gap-2 md:w-fit">
                  <TachoRosterFilters
                    tab={tab}
                    filters={filterValues}
                    onChange={setFilters}
                  />
                  <div
                    className="bg-border my-auto h-6 w-px shrink-0"
                    aria-hidden
                  />
                  <Button
                    variant="outline"
                    className="min-w-0 gap-2 px-3 has-[>svg]:px-3 max-md:h-11 sm:shrink-0 sm:px-4 sm:has-[>svg]:px-4"
                    onClick={() => setSchedulesOpen(true)}
                  >
                    <CalendarClock className="size-4 shrink-0" />
                    <span className="truncate">{ts('schedulesButton')}</span>
                  </Button>
                </div>
              </div>
            </div>
          </CardHeader>

          <CardContent className="max-md:px-2">
            <TabsContent value="vehicle" className="mt-0">
              {vehiclesLoading ? (
                <div className="overflow-hidden rounded-lg border">
                  <Skeleton className="h-10 w-full rounded-none" />
                  {Array.from({ length: 6 }).map((_, i) => (
                    <Skeleton
                      key={i}
                      className="h-14 w-full rounded-none border-t"
                    />
                  ))}
                </div>
              ) : !vehiclesData?.items.length ? (
                <EmptyState
                  icon={Truck}
                  message={
                    hasActiveFilters ? t('noVehiclesMatch') : t('noVehicles')
                  }
                />
              ) : (
                <>
                  {selectionBar}
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:hidden">
                    {sortedVehicles.map((vehicle) => (
                      <TachographVehicleCard
                        key={vehicle.id}
                        vehicle={vehicle}
                        selected={selectedVehicleIds.includes(vehicle.id)}
                        onToggleSelect={(checked) =>
                          toggleVehicle(vehicle.id, checked)
                        }
                        onQuickSchedule={() => openQuick([vehicle.id], false)}
                        assignment={assignmentMap.get(vehicle.id)}
                        assignmentLoading={assignmentsLoading}
                      />
                    ))}
                  </div>

                  <div className="hidden overflow-hidden rounded-lg border lg:block">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50 hover:bg-muted/50">
                          <TableHead className="w-10">
                            <Checkbox
                              checked={allVehiclesSelected}
                              onCheckedChange={(v) =>
                                toggleAllVehicles(v === true)
                              }
                              aria-label={ts('selectAll')}
                            />
                          </TableHead>
                          <SortableTableHead
                            sortKey="licensePlate"
                            sort={vehicleSort}
                            onSort={handleVehicleSort}
                          >
                            {t('table.vehicle')}
                          </SortableTableHead>
                          <TableHead>{ts('column.schedules')}</TableHead>
                          <SortableTableHead
                            sortKey="lastDownload"
                            sort={vehicleSort}
                            onSort={handleVehicleSort}
                          >
                            {t('table.lastDownload')}
                          </SortableTableHead>
                          <SortableTableHead
                            sortKey="vin"
                            sort={vehicleSort}
                            onSort={handleVehicleSort}
                          >
                            {t('table.vin')}
                          </SortableTableHead>
                          <SortableTableHead
                            sortKey="status"
                            sort={vehicleSort}
                            onSort={handleVehicleSort}
                          >
                            {t('table.status')}
                          </SortableTableHead>
                          <TableHead className="w-10">
                            <span className="sr-only">
                              {ts('row.quickSchedule')}
                            </span>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortedVehicles.map((vehicle) => (
                          <TachographVehicleTableRow
                            key={vehicle.id}
                            vehicle={vehicle}
                            selected={selectedVehicleIds.includes(vehicle.id)}
                            onToggleSelect={(checked) =>
                              toggleVehicle(vehicle.id, checked)
                            }
                            onQuickSchedule={() =>
                              openQuick([vehicle.id], false)
                            }
                            assignment={assignmentMap.get(vehicle.id)}
                            assignmentLoading={assignmentsLoading}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  <ListPagination
                    page={vehiclesData.page}
                    totalPages={vehiclesData.totalPages}
                    pageSize={vehiclesData.pageSize}
                    totalCount={vehiclesData.totalCount}
                    hasPrevious={vehiclesData.hasPrevious}
                    hasNext={vehiclesData.hasNext}
                    onPageChange={setVehiclePage}
                    onPageSizeChange={(size) => {
                      setVehiclePageSize(size)
                      setVehiclePage(1)
                    }}
                    className="mt-4 px-4 max-md:px-0"
                  />
                </>
              )}
            </TabsContent>

            <TabsContent value="driver" className="mt-0">
              {driversLoading ? (
                <div className="overflow-hidden rounded-lg border">
                  <Skeleton className="h-10 w-full rounded-none" />
                  {Array.from({ length: 6 }).map((_, i) => (
                    <Skeleton
                      key={i}
                      className="h-14 w-full rounded-none border-t"
                    />
                  ))}
                </div>
              ) : !driversData?.items.length ? (
                <EmptyState
                  icon={User}
                  message={
                    hasActiveFilters ? t('noDriversMatch') : t('noDrivers')
                  }
                />
              ) : (
                <>
                  {selectionBar}
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:hidden">
                    {sortedDrivers.map((driver) => (
                      <TachographDriverCard
                        key={driver.id}
                        driver={driver}
                        selected={selectedDriverIds.includes(driver.id)}
                        onToggleSelect={(checked) =>
                          toggleDriver(driver.id, checked)
                        }
                        onQuickSchedule={() => openQuick([driver.id], false)}
                        assignment={assignmentMap.get(driver.id)}
                        assignmentLoading={assignmentsLoading}
                      />
                    ))}
                  </div>

                  <div className="hidden rounded-lg border lg:block">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50 hover:bg-muted/50">
                          <TableHead className="w-10">
                            <Checkbox
                              checked={allDriversSelected}
                              onCheckedChange={(v) =>
                                toggleAllDrivers(v === true)
                              }
                              aria-label={ts('selectAll')}
                            />
                          </TableHead>
                          <SortableTableHead
                            sortKey="name"
                            sort={driverSort}
                            onSort={handleDriverSort}
                          >
                            {t('table.driver')}
                          </SortableTableHead>
                          <TableHead>{ts('column.schedules')}</TableHead>
                          <SortableTableHead
                            sortKey="lastDownload"
                            sort={driverSort}
                            onSort={handleDriverSort}
                          >
                            {t('table.lastDownload')}
                          </SortableTableHead>
                          <SortableTableHead
                            sortKey="tachographCard"
                            sort={driverSort}
                            onSort={handleDriverSort}
                          >
                            {t('table.tachographCard')}
                          </SortableTableHead>
                          <SortableTableHead
                            sortKey="license"
                            sort={driverSort}
                            onSort={handleDriverSort}
                          >
                            {t('table.license')}
                          </SortableTableHead>
                          <SortableTableHead
                            sortKey="status"
                            sort={driverSort}
                            onSort={handleDriverSort}
                          >
                            {t('table.status')}
                          </SortableTableHead>
                          <TableHead className="w-10">
                            <span className="sr-only">
                              {ts('row.quickSchedule')}
                            </span>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortedDrivers.map((driver) => (
                          <TachographDriverTableRow
                            key={driver.id}
                            driver={driver}
                            selected={selectedDriverIds.includes(driver.id)}
                            onToggleSelect={(checked) =>
                              toggleDriver(driver.id, checked)
                            }
                            onQuickSchedule={() =>
                              openQuick([driver.id], false)
                            }
                            assignment={assignmentMap.get(driver.id)}
                            assignmentLoading={assignmentsLoading}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  <ListPagination
                    page={driversData.page}
                    totalPages={driversData.totalPages}
                    pageSize={driversData.pageSize}
                    totalCount={driversData.totalCount}
                    hasPrevious={driversData.hasPrevious}
                    hasNext={driversData.hasNext}
                    onPageChange={setDriverPage}
                    onPageSizeChange={(size) => {
                      setDriverPageSize(size)
                      setDriverPage(1)
                    }}
                    className="mt-4 px-4 max-md:px-0"
                  />
                </>
              )}
            </TabsContent>
          </CardContent>
        </Tabs>
      </Card>

      <SchedulesModal
        open={schedulesOpen}
        onOpenChange={setSchedulesOpen}
        type={scheduleType}
        selectedIds={selectedIds}
        // "Filtered" = all search + filter matches across pages. Count from the
        // paginated total; ids resolved on demand. With nothing active it's the
        // whole (unfiltered) list, which is "All", so Filtered reads 0/disabled.
        filteredCount={filteredCount}
        resolveFilteredIds={resolveFilteredIds}
      />

      <QuickScheduleModal
        open={quickOpen}
        onOpenChange={setQuickOpen}
        type={scheduleType}
        selectedIds={quickTarget.ids}
        onSuccess={quickTarget.fromSelection ? clearSelection : undefined}
      />

      <ConfirmDeleteDialog
        open={removeConfirmOpen}
        onOpenChange={setRemoveConfirmOpen}
        onConfirm={handleConfirmRemove}
        title={ts('removeAssignments.title')}
        description={ts('removeAssignments.description', {
          n: removableEntityCount,
        })}
        cancelLabel={ts('removeAssignments.cancel')}
        confirmLabel={ts('removeAssignments.confirm')}
      />
    </div>
  )
}

function EmptyState({
  icon: Icon,
  message,
}: {
  icon: LucideIcon
  message: string
}) {
  return (
    <div className="text-muted-foreground flex flex-col items-center gap-3 py-16 text-center">
      <Icon className="size-10 opacity-30" />
      <p className="text-sm">{message}</p>
    </div>
  )
}
